import { FC, forwardRef, isValidElement, ReactNode, useCallback, useRef } from "react";
import { Intent, intentToClassName } from "./utils";
import { ToolTip, ToolTipProps } from "./toolTip";
import { useToggleGroup } from "./toggleGroup";
import clsx from "clsx";
import { Badge, BadgeProps } from "./badge";

export type ButtonProps<T extends any = any> = {
  className?: string;
  badge?: BadgeProps;
  children?: ReactNode;
  onClick?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  disabled?: boolean;
  label?: string | ReactNode;
  icon?: ReactNode;
  toolTip?: string | ReactNode | Partial<Omit<ToolTipProps, "children">>;
  toolTipOptions?: Partial<Omit<ToolTipProps, "children">>;
  intent?: Intent
  value?: T;
  selectedClassName?: string;
  selectedIntent?: Intent;
  type?: "button" | "submit" | "reset";
  style?: React.CSSProperties;
  /**
   * Native `title` attribute. Unlike `toolTip` (which wraps the button in a
   * hover-tracking div and never fires on a disabled button), a native title is
   * shown by the browser even when the button is disabled.
   */
  title?: string;
  /**
   * Accessible name. Required for an icon-only button, which has no text for a
   * screen reader to announce.
   */
  ariaLabel?: string;
  /**
   * Stable test hook. Declared explicitly because this prop set is closed and
   * the `<button>` below assigns its attributes one by one: an undeclared
   * `data-*` prop would be a type error and would never reach the DOM.
   */
  "data-testid"?: string;
}

// eslint-disable-next-line react/display-name
export const Button = forwardRef(<T extends any = any>(props: ButtonProps<T>, forwardedRef: React.Ref<HTMLButtonElement>) => {
  const toggleGroup = useToggleGroup();
  const selected = toggleGroup?.selected === props.value;

  const intentClassName = (() => {
    if (props.disabled) return "bg-gray-300 dark:bg-zinc-700 text-gray-700 dark:text-gray-400 cursor-not-allowed";

    return intentToClassName(!selected ? props.intent : props.selectedIntent || props.intent);
  })();

  const handleClick = useCallback((e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (toggleGroup) {
      console.log({
        value: props.value,
        selected: toggleGroup.selected
      }, "Button handleClick");
      toggleGroup.setSelected(props.value || null);
    }

    if (props.onClick) {
      props.onClick(e);
    }
  }
  , [toggleGroup, props.value, props.onClick]);

  const contentDef = (
  <>
    {props.icon && <span className="inline-block">{props.icon}</span>}
      {props.label && <span className="inline-block overflow-hidden text-ellipsis grow shrink">{props.label}</span>}
      {props.children}
    </>
  );

  let buttonDef = <button
    ref={forwardedRef}
    className={clsx(
      "flex flex-row",
      "rounded whitespace-nowrap",
      props.className,
      intentClassName,
      {
        "items-center":  !props.className || props.className?.search("items-") === -1,
        "gap-2 px-2 py-1": props. icon || props.label,
        [props.selectedClassName || ""]: selected
      }
    )}
    style={props.style}
    onClick={handleClick}
    disabled={props.disabled}
    type={props.type || "button"}
    title={props.title}
    aria-label={props.ariaLabel}
    data-testid={props["data-testid"]}
  >
    {contentDef}
  </button>;

  if (props.badge) {
    buttonDef = (
      <Badge {...props.badge}>
        {buttonDef}
      </Badge>
    );
  }

  let toolTipOptions: Omit<ToolTipProps, "children"> = {
    placement: "top",
    text: "",
    ...props.toolTipOptions,
  };
  if (isValidElement(props.toolTip)) {
    toolTipOptions = {
      ...toolTipOptions,
      text: props.toolTip,
    }
  } else if (typeof props.toolTip === "string") {
    toolTipOptions = {
      ...toolTipOptions,
      text: props.toolTip,
    }
  } else if (typeof props.toolTip === "object") {
    toolTipOptions = {
      ...toolTipOptions,
      ...props.toolTip,
    }
  }

  if (props.toolTip) {
    return (
    <ToolTip
      {...toolTipOptions}
    >
      {buttonDef}
    </ToolTip>
    )
  }

  return buttonDef;
}) as <T extends any = any>(props: ButtonProps<T> & { ref?: React.Ref<HTMLButtonElement> }) => React.ReactElement;

export type UploadButtonProps = ButtonProps & {
  accepts?: string;
  onChange: (files: FileList) => void;
}

/**
 * @deprecated Use `UploadButton` from `@greenhouse/ui` instead.
 */
export const UploadButton: FC<UploadButtonProps> = ({ onChange, ...props }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const clickWrapper = useCallback((e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (inputRef.current) {
      inputRef.current.click();
    }

    if (props.onClick) {
      props.onClick(e);
    }
  }, [
    inputRef,
    props.onClick
  ]);

  return (
    <Button {...props} onClick={clickWrapper}>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={props.accepts}
        style={{ display: "none" }}
        onChange={(e) => onChange(e.target.files!)}
      />
    </Button>
  )
}